import {
	Component,
	Inject,
	OnInit,
	ViewChild,
	ElementRef,
} from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialogModule,
} from '@angular/material/dialog';
import { ImageCroppedEvent, ImageCropperModule } from 'ngx-image-cropper';
import { FundraiserService } from 'src/app/pages/fundraiser/services/fundraiser.service';
import { MediaService } from 'src/app/pages/fundraiser/services/media.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatButtonModule } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass, NgIf } from '@angular/common';

@Component({
	selector: 'app-background-image-edit-dialog',
	templateUrl: './background-image-edit-dialog.component.html',
	styleUrls: ['./background-image-edit-dialog.component.scss'],
	standalone: true,
	imports: [
		NgClass,
		ExtendedModule,
		MatButtonModule,
		MatDialogModule,
		NgIf,
		FlexModule,
		ImageCropperModule,
		MatIconModule,
		MatProgressSpinnerModule,
	],
})
export class BackgroundImageEditDialogComponent implements OnInit {
	imageChangedEvent: any = '';
	croppedImage: any = '';
	isEdit: boolean = false;
	isCropperReady: boolean = false;
	fileName!: string;
	isLoading: boolean = false;

	constructor(
		public _fundraiserService: FundraiserService,
		public notificationService: NotificationService,
		public mediaService: MediaService,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			imagePath: any;
			backgroundImageId: any;
			fundraiserId: any;
			slug: any;
		},
		public dialogRef: MatDialogRef<BackgroundImageEditDialogComponent>
	) {}

	ngOnInit(): void {}

	fileChangeEvent(event: Event): void {
		const file = (event.target as HTMLInputElement).files?.[0];
		this.fileName = file?.name || '';
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (e: any) => {
			const img = new Image();
			img.src = e.target.result;
			const imgCopy = new Image();
			imgCopy.src = img.src;

			imgCopy.onload = () => {
				const imageAspect = imgCopy.width / imgCopy.height;
				const canvasAspect = 2 / 1;
				// If image is too tall (won't cover full width)
				if (imageAspect < canvasAspect) {
					this.applyBlurBackground(imgCopy);
				} else {
					// Normal use
					this.imageChangedEvent = event;
					this.isEdit = true;
				}
			};
		};

		reader.readAsDataURL(file);
	}
	applyBlurBackground(img: HTMLImageElement): void {
		const width = 1600;
		const height = 800; // 2:1 ratio

		// Step 1: Create an SVG with a Gaussian blur filter
		const svg = `
		<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
			<filter id="blurFilter">
				<feGaussianBlur in="SourceGraphic" stdDeviation="40" />
			</filter>
			<image x="0" y="0" width="${width}" height="${height}" href="${img.src}" filter="url(#blurFilter)" preserveAspectRatio="xMidYMid slice" />
		</svg>`;
		const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
		const svgUrl = URL.createObjectURL(svgBlob);

		// Step 2: Load blurred image from SVG
		const blurredImg = new Image();
		const originalImg = new Image();
		blurredImg.crossOrigin = 'anonymous';
		originalImg.crossOrigin = 'anonymous';

		blurredImg.onload = () => {
			originalImg.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d');
				if (!ctx) return;

				// Draw blurred background
				ctx.clearRect(0, 0, width, height);
				ctx.drawImage(blurredImg, 0, 0, width, height);

				// Draw centered sharp image (foreground)
				const ratio = Math.min(
					width / originalImg.width,
					height / originalImg.height
				);
				const newWidth = originalImg.width * ratio;
				const newHeight = originalImg.height * ratio;
				const offsetX = (width - newWidth) / 2;
				const offsetY = (height - newHeight) / 2;

				ctx.drawImage(originalImg, offsetX, offsetY, newWidth, newHeight);

				// Step 3: Export to file and trigger fake event
				canvas.toBlob((blob) => {
					if (!blob) return;

					const newFile = new File([blob], 'modified.png', {
						type: 'image/png',
					});
					const fakeEvent = {
						target: {
							files: [newFile],
						},
					};

					this.imageChangedEvent = fakeEvent;
					this.isEdit = true;

					// Clean up
					URL.revokeObjectURL(svgUrl);
				}, 'image/png');
			};

			// Load original image after blurred is loaded
			originalImg.src = img.src;
		};

		blurredImg.src = svgUrl;
	}

	imageCropped(event: ImageCroppedEvent) {
		this.isEdit = true;
		this.croppedImage = event.base64;
	}

	cropperReady() {
		// cropper ready
		this.isCropperReady = true;
	}

	loadImageFailed() {}

	openFile(fileEvent: any) {
		fileEvent.click();
	}

	dialogClose() {
		this.isLoading = true;
		try {
			let backgroundPayload = {
				image: this.mediaService.base64toBlob(this.croppedImage),
				slug: this.data?.slug,
			};

			let backgroundVideoPayload = {
				video: '',
				slug: this.data?.slug,
			};
			//Update Background----------------------------------------------------------
			this._fundraiserService
				.updateFundraiserBackground(backgroundPayload)
				.subscribe(
					(res) => {
						this._fundraiserService
							.addVideoBackground(backgroundVideoPayload)
							.then((res) => {
								this.isLoading = false;
								this.notificationService.openNotification(
									$localize`:@@background_image_fundraiserBackground_notification:Fundraiser background image is saved`,
									'',
									'success'
								);

								window.location.reload();
							});

						// this.dialogRef.close(this.croppedImage);
					},
					(err: any) => {
						this.notificationService.openNotification(
							$localize`:@@edit_created_errorUpdatingBackground_notification:There was an error updating background`,
							'close',
							'error'
						);
						this.isLoading = false;
					}
				);
		} catch (err: any) {
			this.notificationService.openNotification(
				$localize`:@@edit_created_errorUpdatingBackground_notification:There was an error updating background`,
				'close',
				'error'
			);
			this.isLoading = false;
		}
	}

	onNoClick() {
		this.dialogRef.close();
	}
}
